/**
 * @fileoverview Account apps UI — the Spanish (ES) bundle (APP-ENTITLEMENT-001 Phase 9).
 * @package @puffin-core/account-apps-ui
 *
 * Leaf-for-leaf mirror of `en.js`; `_meta.js` marks every leaf `'machine'` until a native-speaker review flips it to
 * `'human'`.
 */

export const ACCOUNT_APPS_UI_ES = Object.freeze({
  // The page
  heading: 'Apps y facturación',
  anchorHeading: 'Su app principal',
  appsHeading: 'Apps',
  billingHeading: 'Facturación',
  loading: 'Cargando sus apps…',
  loadFailed: 'No se pudieron cargar sus apps. Inténtelo de nuevo.',
  retry: 'Intentar de nuevo',
  noAnchor: 'Esta cuenta aún no tiene una app principal. Pida a ParcelPuffin que la configure.',
  noPermission: 'Pida a quien administra las apps de su cuenta.',
  readOnlyBanner:
    'Algunas apps son de solo lectura: sus pruebas terminaron. Conserve cada una con una tarjeta para volver a trabajar en ella.',
  waivedNote: 'Las apps de cortesía nunca se facturan.',

  // Every app's state
  stateActive: 'Activa',
  stateTrialing: 'Prueba',
  stateReadOnly: 'Solo lectura',
  stateAvailable: 'Disponible',
  stateComingSoon: 'Próximamente',
  stateIncluded: 'Incluida',
  stateComplimentary: 'Cortesía',
  stateOff: 'Apagada',
  trialEndsOn: 'La prueba termina el',
  trialEnded: 'La prueba terminó. Consérvela con una tarjeta para usarla de nuevo.',
  comingSoonHint: 'Todavía no está a la venta. Le avisaremos cuando lo esté.',
  onlyWithAnchor: 'Va con {anchors}',

  // Prices
  pricePerUnitMonthly: '{price} por {unit} al mes',
  priceFlatMonthly: '{price} al mes',
  pricePerBlock: '{price} por {blockSize} {units}',
  free: 'Gratis con su app principal',
  shareOfRecovery: '{percent}% de los reembolsos recuperados, sin cuota mensual',
  includedUnits: '{count} {units} incluidos',
  priceUnpublished: 'Precio aún no publicado',

  // Size dimensions, singular and plural
  unitAccount: 'cuenta',
  unitLocation: 'tienda',
  unitRegister: 'caja',
  unitSite: 'sitio',
  unitLine: 'línea telefónica',
  unitAiReceptionist: 'recepcionista de IA',
  unitMailbox: 'buzón',
  unitEmployeePaid: 'empleado pagado',
  unitStorefront: 'tienda en línea',
  unitOffice: 'oficina',
  unitTechnician: 'técnico',
  unitsAccount: 'cuenta',
  unitsLocation: 'tiendas',
  unitsRegister: 'cajas',
  unitsSite: 'sitios',
  unitsLine: 'líneas telefónicas',
  unitsAiReceptionist: 'recepcionistas de IA',
  unitsMailbox: 'buzones',
  unitsEmployeePaid: 'empleados pagados',
  unitsStorefront: 'tiendas en línea',
  unitsOffice: 'oficinas',
  unitsTechnician: 'técnicos',

  // Consumables
  consumableBlogPosts: 'entradas de blog',
  consumableAiMinutes: 'minutos de IA',
  consumableSmsSegments: 'segmentos de texto',
  consumableAiGenerations: 'generaciones de IA',
  consumableAiQualifications: 'calificaciones de IA',
  consumableAiOutcomes: 'resultados de IA',

  // Actions
  startTrial: 'Iniciar una prueba de {days} días',
  addApp: 'Agregar',
  turnOn: 'Activar',
  turnOff: 'Desactivar',
  keep: 'Conservarla',
  remove: 'Quitar',
  manageLines: 'Líneas y topes',
  openApp: 'Abrir',
  working: 'Procesando…',
  cancel: 'Cancelar',
  confirm: 'Confirmar',
  save: 'Guardar',
  close: 'Cerrar',

  // Confirming a change
  addTitle: '¿Agregar {app}?',
  addTrialBody:
    'Su prueba de {days} días empieza ahora. No se necesita tarjeta. Cuando termine, {app} queda en solo lectura hasta que la conserve con una tarjeta.',
  addChargeBody: '{app} cuesta {monthly} al mes y se cobra con su próxima factura.',
  proratedToday: 'Hoy: {amount} por el resto de este período.',
  periodEnd: 'Próxima factura el',
  keepTitle: '¿Conservar {app}?',
  keepBody: 'A partir de ahora se cobran {monthly} al mes a su tarjeta.',
  keepNoCard: 'Agregue una tarjeta primero y luego conserve la app.',
  removeTitle: '¿Quitar {app}?',
  removeBody: 'Sus pantallas se cierran y deja de facturarse. Sus datos se conservan.',
  removePhrase: 'QUITAR',
  removePrompt: 'Escriba {phrase} para confirmar.',
  turnOffTitle: '¿Desactivar {app}?',
  turnOffBody: 'Los compradores ya no podrán iniciar sesión en el sitio de esta cuenta.',
  previewFailed: 'No se pudo calcular el precio. Inténtelo de nuevo.',

  // Lines and caps
  linesTitle: '{app}: líneas y topes',
  derivedQuantityHint:
    'Se cuenta desde su cuenta: agregue o quite uno en {app} y esta línea lo sigue.',
  declaredQuantityLabel: 'Cuántos {units}',
  includedWithParent: '{count} {units} incluidos con cada {parentUnit}',
  capLabel: 'Tope de {consumable} adicionales',
  capHint:
    '{included} {consumable} incluidos por {parentUnit}; los demás vienen en bloques de {blockSize}. Fije el máximo de bloques que pagará en un mes. 0 se detiene en lo incluido.',
  capBlocks: 'bloques',
  lineSaved: 'Guardado.',

  // Refusals, worded by CODE
  refusalAnchorRequired: 'Elija primero una app principal.',
  refusalAppNotAttachable: 'Esta app no va con su app principal.',
  refusalAppNotSellable: 'Esta app no se ofrece.',
  refusalAppUnknown: 'No existe esa app.',
  refusalDimensionUnknown: 'Esta app no tiene esa línea.',
  refusalQuantityBelowIncluded: 'Eso es menos de lo que incluye su app principal.',
  refusalPriceNotPublished: 'Esta app aún no tiene precio publicado.',
  refusalPriceEndingRefused: 'No se pudo aplicar el precio.',
  refusalCapReached: 'Se alcanzó el tope de este período.',
  refusalMeterUnavailable: 'Este uso lo cuenta la propia app. Cambie el tope desde allí.',
  refusalCapabilityDependencyUnmet: 'Este cambio deja a una app sin algo de lo que depende.',
  refusalCapBelowConsumed: 'Un tope no puede bajar de lo usado en este período.',
  refusalAlreadyEntitled: 'Ya tiene esta app.',
  refusalNotEntitled: 'No tiene esta app.',
  refusalNotTrialing: 'Esta app no está en prueba.',
  refusalNoPaymentMethod: 'Agregue una tarjeta primero.',
  refusalNoBillingHead: 'Esta cuenta no tiene registro de facturación. Consulte a ParcelPuffin.',
  refusalComplimentaryEntitlement: 'Esta app es de cortesía. Pida a ParcelPuffin que la cambie.',
  refusalIncludedAppNotBillable: 'Esta app es gratis con su app principal.',
  refusalIncludedAppFollowsAnchor: 'Esta app viene con su app principal y se va con ella.',
  refusalAnchorRemovalBlocked: 'Quite las otras apps antes que la principal.',
  refusalPriceConfirmationRequired: 'Confirme el precio primero.',
  pendingChargeMonthly: 'Este cambio añade {price} al mes a la factura de la cuenta.',
  pendingChargeAnnual: 'Este cambio añade {price} al año a la factura de la cuenta.',
  acceptCharge: 'Aceptar el cargo',
  refusalComplimentaryAccount:
    'Esta cuenta es de cortesía: no admite tarjeta ni ciclo de facturación.',
  refusalBillingCycleUnchanged: 'Ya se le factura en ese ciclo.',
  refusalCardDeclined: 'La tarjeta fue rechazada.',
  refusalCustomerAccountsInUse:
    'Hay clientes que han iniciado sesión en el sitio de esta cuenta, así que las cuentas de clientes siguen activas.',
  refusalForbidden: 'No tiene permiso para cambiar las apps de esta cuenta.',
  refusalUnknown: 'No se hizo el cambio. Inténtelo de nuevo.',

  // Billing
  paymentMethodHeading: 'Método de pago',
  cardSummary: '{brand} terminada en {last4} (vence {expMonth}/{expYear})',
  noPaymentMethod: 'No hay tarjeta registrada.',
  complimentaryNoCard: 'No se necesita tarjeta: esta cuenta es de cortesía.',
  stripeUnavailable: 'Los datos de tarjeta y facturas no están disponibles en este momento.',
  addCard: 'Agregar tarjeta',
  addCardDescription:
    'Se cobra a su tarjeta por las apps que conserve después de sus pruebas, al precio publicado.',
  cardAria: 'Tarjeta de crédito o débito',
  cardPlaceholder: 'Número de tarjeta, MM/AA, CVC',
  cardHelp: 'Los datos de la tarjeta van directo al procesador de pagos y nunca llegan a esta app.',
  saveCard: 'Guardar tarjeta',
  savingCard: 'Guardando tarjeta…',
  cardSaveFailed: 'No se pudo guardar la tarjeta. Inténtelo de nuevo.',
  cardNotReady: 'La captura de tarjeta no está lista. Actualice e inténtelo de nuevo.',
  cardNoElement: 'La captura de tarjeta no está disponible. Actualice e inténtelo de nuevo.',
  cardEntryUnconfigured: 'La captura de tarjeta aún no está configurada en esta app.',
  cycleHeading: 'Ciclo de facturación',
  cycleMonthly: 'Mensual',
  cycleAnnual: 'Anual',
  cycleAnnualNote: 'El anual es el precio de diez meses por doce meses.',
  switchToMonthly: 'Cambiar a mensual',
  switchToAnnual: 'Cambiar a anual',
  cycleChanged: 'Ciclo de facturación cambiado.',
  nextInvoiceHeading: 'Su próxima factura',
  quoteMonthly: '{amount} al mes',
  quoteAnnual: '{amount} al año ({monthly} al mes)',
  quoteUnavailable: 'Aún no se puede mostrar un total: {reason}',
  invoicesHeading: 'Facturas recientes',
  noInvoices: 'Aún no hay facturas.',
  colDate: 'Fecha',
  colInvoice: 'Factura',
  colStatus: 'Estado',
  colAmount: 'Importe',
  colReceipt: 'Recibo',
  view: 'Ver',

  // Suggestions
  suggestionsHeading: 'Combina bien con lo que tiene',
  suggestionMailOnYourDomain:
    'Correo en su propio dominio: buzones ProntoPuffin para las personas detrás de su tienda en línea.',
  suggestionAnswerEveryCall:
    'Atienda todas las llamadas: la recepcionista de IA toma las que su equipo no puede.',
  suggestionSchedulesIntoPayroll:
    'Horarios directo a la nómina: las horas de PlannerPuffin se vuelven las tarjetas de tiempo de PayDayPuffin.',
  suggestionAction: 'Agregar {app}',

  // The anchor card
  anchorIncludesHeading: 'Incluido con {anchor}',
  freeAppsHeading: 'Gratis con su app principal',
  featurePos: 'Punto de venta',
  featureWebsite: 'Un sitio web en su propio dominio',
  featureHosting: 'Alojamiento',
  featureBlog: 'Blog automático',
  featureOwnerConsole: 'Consola del propietario',
  featureAnalytics: 'Analítica',
  featurePageAccess: 'Páginas restringidas y un portal de clientes',
  featureFieldService: 'Servicio en campo',
});
